import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";

var _Loading, _dec, _class, _class2;

const _excluded = ["open"];

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

/** @jsx jsx */
import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import screenfull from 'screenfull';
import { Children as ChildrenPropTypes } from '@instructure/ui-prop-types';
import { FocusRegionManager } from '@instructure/ui-a11y-utils';
import { pickProps } from '@instructure/ui-react-utils';
import { withStyle, jsx } from '@instructure/emotion';
import uid from '@instructure/uid';
import PlayerContext from './PlayerContext';
import PlayerControls from '../PlayerControls';
import PlayerMarker from '../PlayerMarker';
import PlayerOverlay from '../PlayerOverlay';
import PlayerPlayhead from '../PlayerPlayhead';
import { Captions } from './Captions';
import PlayerPropTypes, { playerEventsTypeSet, rendererEventsTypeSet, sourcesType, tracksType } from './PropTypes';
import { applyTranslations, translate, TranslationPropTypes } from '../../constants/translated/translations';
import Inert from './Inert';
import MediaContext from './MediaContext';
import Loading from '../Loading';
import VolumeIndication from '../VolumeIndication';
import { PAUSED, PLAYING, ENDED, WINDOWED_SCREEN, FULL_SCREEN, captionPositions, captionColors, captionFontSizes } from '../../constants';
import { calculateVolume, clamp } from '../utils';
import generateStyle from './styles';
import generateComponentTheme from './theme';
export const SEEK_INTERVAL_SECONDS = 5;
export const JUMP_INTERVAL_SECONDS = 30;
export const SEEK_VOLUME_INTERVAL = 0.05;
export const JUMP_VOLUME_INTERVAL = 0.1;
/**
---
category: components
experimental: true
---
**/

const WrappedPlayerOverlay = props => jsx(MediaContext.Consumer, null, _ref => {
  let actions = _ref.actions;
  return jsx(PlayerOverlay, Object.assign({
    setFocusEnabled: actions.setFocusEnabled
  }, props));
});

const LoadingOverlay = _ref2 => {
  let open = _ref2.open,
      props = _objectWithoutProperties(_ref2, _excluded);

  return open && jsx(PlayerOverlay, props, () => _Loading || (_Loading = jsx(Loading, null)));
};

const VolumeIndicationOverlay = props => jsx(PlayerOverlay, {
  disablePointerEvents: true
}, () => jsx(VolumeIndication, props));

let Player = (_dec = withStyle(generateStyle, generateComponentTheme), _dec(_class = (_class2 = class Player extends PureComponent {
  constructor(props) {
    var _this;

    super(props);
    _this = this;
    this.focusRegion = null;
    this.fullScreenContainer = null;
    this.videoContainer = null;
    this.video = null;
    this.state = _objectSpread(_objectSpread({
      seeking: false,
      loadingOverlay: false,
      controlActivated: null,
      controlHovered: false,
      focusEnabled: true,
      mediaState: PAUSED,
      screenState: WINDOWED_SCREEN,
      muted: false,
      currentTime: this.props.startAt || 0,
      selectedSrc: this.getSourceFromProps(),
      sources: this.props.sources,
      showControls: true,
      videoId: uid('Player'),
      lastTrackId: null
    }, this.getPlayerSettings()), {}, {
      volumeIndicatorTrigger: false
    });

    this.clearFocusRegion = () => {
      if (!this.focusRegion) return;
      FocusRegionManager.blurRegion(this.fullScreenContainer, this.focusRegion.id);
      this.focusRegion = null;
    };

    this.handleSeeking = () => {
      this.setState({
        seeking: true
      });
    };

    this.handleSeeked = () => {
      this.setState({
        seeking: false
      });
    };

    this.handleContextMenu = e => {
      if (this.props.disableRightClick) {
        e.preventDefault();
      }
    };

    this.handleKeyPress = actions => e => {
      const _this$state = this.state,
            currentTime = _this$state.currentTime,
            duration = _this$state.duration,
            volume = _this$state.volume;
      const seek = actions.seek,
            togglePlay = actions.togglePlay,
            toggleFullScreen = actions.toggleFullScreen,
            toggleMute = actions.toggleMute,
            setVolume = actions.setVolume,
            showControls = actions.showControls;

      const playAction = () => {
        if (this.videoContainer === e.target) {
          togglePlay();
          return true;
        }

        return false;
      };

      const seekAction = time => {
        if (!this.state.controlActivated) {
          seek(time);
          return true;
        }

        return false;
      };

      const keyHandlers = {
        ArrowLeft: () => {
          return seekAction(currentTime - SEEK_INTERVAL_SECONDS);
        },
        ArrowRight: () => {
          return seekAction(currentTime + SEEK_INTERVAL_SECONDS);
        },
        ArrowUp: () => {
          setVolume(calculateVolume(volume, SEEK_VOLUME_INTERVAL));
          this.triggerVolumeIndicator();
          return true;
        },
        ArrowDown: () => {
          setVolume(calculateVolume(volume, -SEEK_VOLUME_INTERVAL));
          this.triggerVolumeIndicator();
          return true;
        },
        PageUp: () => {
          seek(currentTime + JUMP_INTERVAL_SECONDS);
          return true;
        },
        PageDown: () => {
          seek(currentTime - JUMP_INTERVAL_SECONDS);
          return true;
        },
        Home: () => {
          seek(0);
          return true;
        },
        End: () => {
          seek(duration);
          return true;
        },
        ' ': playAction,
        Enter: playAction,
        f: () => {
          toggleFullScreen();
          return true;
        },
        F: () => {
          toggleFullScreen();
          return true;
        },
        m: () => {
          toggleMute();
          return true;
        },
        M: () => {
          toggleMute();
          return true;
        },
        0: () => {
          return seekAction(0);
        },
        1: () => {
          return seekAction(duration * 0.1);
        },
        2: () => {
          return seekAction(duration * 0.2);
        },
        3: () => {
          return seekAction(duration * 0.3);
        },
        4: () => {
          return seekAction(duration * 0.4);
        },
        5: () => {
          return seekAction(duration * 0.5);
        },
        6: () => {
          return seekAction(duration * 0.6);
        },
        7: () => {
          return seekAction(duration * 0.7);
        },
        8: () => {
          return seekAction(duration * 0.8);
        },
        9: () => {
          return seekAction(duration * 0.9);
        }
      };

      if (e.key in keyHandlers && keyHandlers[e.key]()) {
        e.preventDefault();
        showControls();
      }
    };

    this.setFullScreen = newState => {
      if (screenfull && screenfull.enabled) {
        if (newState && this.state.screenState === WINDOWED_SCREEN) {
          screenfull.request(this.fullScreenContainer);
        } else if (!newState && this.state.screenState === FULL_SCREEN) {
          screenfull.exit();
        }
      }
    };

    this.toggleFocusRegion = () => {
      if (this.state.screenState === FULL_SCREEN) {
        this.focusRegion = FocusRegionManager.activateRegion(this.fullScreenContainer, {
          shouldCloseOnDocumentClick: false,
          shouldCloseOnEscape: true,
          shouldContainFocus: true,
          shouldReturnFocus: true,
          onBlur: () => {},
          onDismiss: () => {}
        });
        return;
      }

      this.clearFocusRegion();
    };

    this.toggleFullScreen = () => {
      if (screenfull && screenfull.enabled) {
        screenfull.toggle(this.fullScreenContainer);
      }
    };

    this.updateScreenState = e => {
      if (!this.fullScreenContainer) {
        return;
      }

      const screenState = screenfull && screenfull.isFullscreen ? FULL_SCREEN : WINDOWED_SCREEN;
      this.setState({
        screenState
      });

      if (e.target === this.fullScreenContainer) {
        this.toggleFocusRegion();
      }
    };

    this.setVideoContainerRef = el => {
      if (this.videoContainer === null) {
        this.videoContainer = el;
      }
    };

    this.setFullScreenContainerRef = el => {
      if (this.fullScreenContainer === null) {
        this.fullScreenContainer = el;
      }
    };

    this.activateControl = controlId => {
      this.setState({
        controlActivated: controlId
      });
    };

    this.deactivateControl = controlId => {
      this.setState(prevState => {
        if (prevState.controlActivated === controlId) {
          return {
            controlActivated: null
          };
        }

        return {};
      });
    };

    this.setControlHovered = newValue => {
      this.setState({
        controlHovered: newValue
      });
    };

    this.setFocusEnabled = newValue => {
      this.setState({
        focusEnabled: newValue
      });
    };

    this.showControls = function () {
      let hideControlsTimeout = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : 2500;

      if (_this.props.alwaysShowControls) {
        return;
      }

      if (_this._hideControlsTimeoutId) {
        clearTimeout(_this._hideControlsTimeoutId);
      }

      const _nextTimeout = () => {
        _this._hideControlsTimeoutId = setTimeout(() => {
          if (_this.state.controlActivated || _this.state.controlHovered) {
            return _nextTimeout();
          }

          if (_this.state.mediaState === PLAYING) {
            _this.setState({
              showControls: false
            });

            _this.props.onControlsHidden();
          }
        }, hideControlsTimeout);
      };

      const prevValue = _this.state.showControls;

      _this.setState({
        showControls: true
      }, _nextTimeout);

      if (!prevValue) {
        _this.props.onControlsShown();
      }
    };

    this.onRendererStateChange = function (rendererStateUpdater) {
      let callback = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : () => {};

      _this.setState(rendererStateUpdater, callback);
    };

    this.setActions = (actions, _ref3) => {
      let setAction = _ref3.setAction;
      Object.entries(actions).forEach(_ref4 => {
        let _ref5 = _slicedToArray(_ref4, 2),
            fnName = _ref5[0],
            fn = _ref5[1];

        setAction(fnName, fn);
      });
    };

    this.onTrackChange = selectedTrackId => {
      this.props.savePlayerSettings({
        isCaptionOn: !!selectedTrackId
      });

      if (this.state.selectedTrackId !== null && selectedTrackId === null) {
        this.setState({
          lastTrackId: this.state.selectedTrackId
        });
      }

      this.setState({
        selectedTrackId
      });
    };

    this.toggleCaptionPosition = () => {
      this.setState(prevState => ({
        captionPosition: prevState.captionPosition === captionPositions.TOP ? captionPositions.BOTTOM : captionPositions.TOP
      }), () => this.props.savePlayerSettings({
        captionPosition: this.state.captionPosition
      }));
    };

    this.toggleCaptionColor = () => {
      this.setState(prevState => ({
        captionColor: prevState.captionColor === captionColors.DARK ? captionColors.LIGHT : captionColors.DARK
      }), () => this.props.savePlayerSettings({
        captionColor: this.state.captionColor
      }));
    };

    this.setCaptionFontSize = size => {
      this.setState({
        captionFontSize: size
      }, () => this.props.savePlayerSettings({
        captionFontSize: size
      }));
    };

    this.setLoadingOverlay = loadingOverlay => {
      this.setState({
        loadingOverlay
      });
    };

    applyTranslations(this.props.translations);
  }

  componentDidMount() {
    this.props.makeStyles({
      screenState: this.state.screenState
    });
    screenfull && screenfull.on('change', this.updateScreenState);
  }

  componentDidUpdate(prevProps, prevState) {
    const _this$state2 = this.state,
          screenState = _this$state2.screenState,
          mediaState = _this$state2.mediaState;
    const _this$props = this.props,
          fluidWidth = _this$props.fluidWidth,
          fluidHeight = _this$props.fluidHeight;
    /*
     * Optimize styles recalculations in addition to extendind PureComponent.
     * Without this optimization we get tones of recalculations.
     * Try to put console log into generateStyle funtion.
     */

    if (screenState !== prevState.screenState || fluidWidth !== prevProps.fluidWidth || fluidHeight !== prevProps.fluidHeight) {
      this.props.makeStyles({
        screenState
      });
    }

    if (prevState.mediaState !== mediaState && mediaState === ENDED) {
      this.showControls();
    }
  }

  componentWillUnmount() {
    screenfull && screenfull.off('change', this.updateScreenState); // remove the video ref and stop applying video props

    this.videoContainer = null;
    this.fullScreenContainer = null;
    this.clearFocusRegion();
  }

  getSourceFromProps() {
    const sources = this.props.sources;

    if (sources.length === 0) {
      return null;
    }

    const defaultSelected = sources.find(s => !!s.defaultSelected);
    return defaultSelected ? defaultSelected.src : sources[0].src;
  }

  getCaptionPosition(settings) {
    return [captionPositions.BOTTOM, captionPositions.TOP].includes(settings === null || settings === void 0 ? void 0 : settings.captionPosition) ? settings === null || settings === void 0 ? void 0 : settings.captionPosition : captionPositions.BOTTOM;
  }

  getCaptionColor(settings) {
    return [captionColors.DARK, captionColors.LIGHT].includes(settings === null || settings === void 0 ? void 0 : settings.captionColor) ? settings === null || settings === void 0 ? void 0 : settings.captionColor : captionColors.DARK;
  }

  getCaptionFontSize(settings) {
    return [captionFontSizes.NORMAL, captionFontSizes.LARGE, captionFontSizes.X_LARGE].includes(settings === null || settings === void 0 ? void 0 : settings.captionFontSize) ? settings === null || settings === void 0 ? void 0 : settings.captionFontSize : captionFontSizes.NORMAL;
  }

  getSelectedCaptionId(settings) {
    const _this$props2 = this.props,
          tracks = _this$props2.tracks,
          autoShowCaption = _this$props2.autoShowCaption,
          defaultTrackId = _this$props2.defaultTrackId;

    if (settings !== null && settings !== void 0 && settings.isCaptionOn && Array.isArray(tracks) && tracks.length) {
      var _tracks$find;

      return defaultTrackId || ((_tracks$find = tracks.find(t => t.srclang == autoShowCaption)) === null || _tracks$find === void 0 ? void 0 : _tracks$find.id) || tracks[0].id;
    }

    return null;
  }

  getPlayerSettings() {
    var _settings$volume, _settings$playbackSpe;

    const settings = this.props.getSettings();
    return {
      captionPosition: this.getCaptionPosition(settings),
      captionColor: this.getCaptionColor(settings),
      captionFontSize: this.getCaptionFontSize(settings),
      selectedTrackId: this.getSelectedCaptionId(settings),
      volume: clamp((_settings$volume = settings === null || settings === void 0 ? void 0 : settings.volume) !== null && _settings$volume !== void 0 ? _settings$volume : 1, 0, 1),
      playbackSpeed: clamp((_settings$playbackSpe = settings === null || settings === void 0 ? void 0 : settings.playbackSpeed) !== null && _settings$playbackSpe !== void 0 ? _settings$playbackSpe : 1, 0.5, 2)
    };
  }

  triggerVolumeIndicator() {
    this.setState(prevState => ({
      volumeIndicatorTrigger: !prevState.volumeIndicatorTrigger
    }));
  }

  render() {
    var _LoadingOverlay;

    const _this$props3 = this.props,
          Renderer = _this$props3.renderer,
          controls = _this$props3.controls,
          actionsRef = _this$props3.actionsRef,
          styles = _this$props3.styles,
          tracks = _this$props3.tracks;
    const _this$state3 = this.state,
          focusEnabled = _this$state3.focusEnabled,
          loadingOverlay = _this$state3.loadingOverlay,
          volume = _this$state3.volume,
          captionPosition = _this$state3.captionPosition,
          showControls = _this$state3.showControls,
          selectedSrc = _this$state3.selectedSrc,
          selectedTrackId = _this$state3.selectedTrackId,
          volumeIndicatorTrigger = _this$state3.volumeIndicatorTrigger;
    const constants = {
      SEEK_INTERVAL_SECONDS,
      JUMP_INTERVAL_SECONDS,
      SEEK_VOLUME_INTERVAL,
      JUMP_VOLUME_INTERVAL
    };
    return jsx(PlayerContext, null, playerContext => {
      const actionsSetter = actions => {
        this.setActions(actions, playerContext);
        actionsRef && actionsRef(playerContext.actions);
      };

      actionsSetter({
        activateControl: this.activateControl,
        deactivateControl: this.deactivateControl,
        setFullScreen: this.setFullScreen,
        toggleFullScreen: this.toggleFullScreen,
        setControlHovered: this.setControlHovered,
        setFocusEnabled: this.setFocusEnabled,
        showControls: this.showControls,
        toggleCaptionPosition: this.toggleCaptionPosition,
        toggleCaptionColor: this.toggleCaptionColor,
        setCaptionFontSize: this.setCaptionFontSize,
        savePlayerSettings: this.props.savePlayerSettings
      });
      const mediaContextValue = {
        state: _objectSpread(_objectSpread({}, this.state), {}, {
          tracks
        }),
        fullScreenContainerRef: () => this.fullScreenContainer,
        actions: playerContext.actions,
        setActions: actionsSetter,
        playbackSpeedOptions: playerContext.playbackSpeedOptions,
        constants
      };
      const fullScreenContainerProps = {
        css: styles.fullScreenContainer,
        ref: this.setFullScreenContainerRef
      };
      const mediaPlayerContainerProps = {
        css: styles.mediaPlayerContainer,
        onKeyDown: this.handleKeyPress(playerContext.actions),
        onFocus: () => this.showControls(),
        onMouseMove: () => this.showControls(),
        onClick: playerContext.actions.togglePlay,
        tabIndex: focusEnabled ? 0 : null,
        // removes ability to focus container when focus disabled
        role: 'presentation',
        'aria-label': this.props.label || translate('ARIA_VIDEO_LABEL'),
        ref: this.setVideoContainerRef
      };
      const defaultProps = {
        onSeeking: this.handleSeeking,
        onSeeked: this.handleSeeked
      };
      const rendererEventsTypeSetFromProps = pickProps(this.props, rendererEventsTypeSet);
      const eventProps = {};
      const allEvents = [...Object.keys(rendererEventsTypeSetFromProps), ...Object.keys(defaultProps)];
      allEvents.forEach(key => {
        const propFn = rendererEventsTypeSetFromProps[key];
        const defaultPropFn = defaultProps[key];

        eventProps[key] = (event, error) => {
          if (propFn) {
            propFn(event, _objectSpread(_objectSpread({}, this.state), {}, {
              tracks
            }), error);
          }

          if (defaultPropFn) {
            defaultPropFn(event);
          }
        };
      });
      return jsx("div", fullScreenContainerProps, jsx("div", mediaPlayerContainerProps, jsx("div", {
        css: styles.playerRendererContainer,
        onContextMenu: this.handleContextMenu
      }, jsx(Renderer, Object.assign({
        poster: this.props.poster,
        videoId: this.state.videoId,
        selectedSrc: selectedSrc,
        setActions: actionsSetter,
        onRendererStateChange: this.onRendererStateChange,
        mediaState: this.state.mediaState,
        currentTime: this.state.currentTime || 0,
        setPlaybackSpeedOptions: playerContext.setPlaybackSpeedOptions,
        setLoadingOverlay: this.setLoadingOverlay,
        disableYoutubeFocus: this.props.disableYoutubeFocus,
        volume: this.state.volume,
        playbackSpeed: this.state.playbackSpeed
      }, eventProps)), jsx(Captions, {
        tracks: tracks,
        defaultTrack: this.state.selectedTrackId,
        onTrackChange: this.onTrackChange,
        setActions: actionsSetter,
        currentTime: this.state.currentTime || 0,
        captionColor: this.state.captionColor,
        captionPosition: captionPosition,
        captionOffset: this.props.captionOffset,
        captionFontSize: this.state.captionFontSize,
        autoShowCaption: this.props.autoShowCaption,
        showControls: showControls
      })), jsx(MediaContext.Provider, {
        value: mediaContextValue
      }, jsx("div", null, this.props.children, _LoadingOverlay || (_LoadingOverlay = jsx(LoadingOverlay, {
        open: loadingOverlay
      })), jsx(VolumeIndicationOverlay, {
        volume: volume,
        position: captionPosition === captionPositions.TOP && !!selectedTrackId ? captionPositions.BOTTOM : captionPositions.TOP,
        showControls: showControls,
        trigger: volumeIndicatorTrigger
      }), !this.props.hideControls && jsx(Inert, {
        active: !focusEnabled
      }, controls(PlayerControls))))));
    });
  }

}, _class2.propTypes = _objectSpread(_objectSpread({
  // eslint-disable-next-line react/require-default-props
  makeStyles: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  styles: PropTypes.object,
  renderer: PropTypes.elementType.isRequired,

  /**
   * URL(s) of video to play
   */
  sources: sourcesType,

  /**
   * tracks of the video to play
   */
  tracks: tracksType,

  /**
   * Default selected caption's track id
   */
  // eslint-disable-next-line react/require-default-props
  defaultTrackId: PropTypes.string,

  /**
   * Function invoked on every render with state and actions.
   * Use this to provide a custom set of video controls.
   * Default player controls will be provided if undefined.
   */
  controls: PropTypes.func,

  /**
   * If set to true, the controls will never dismiss.
   */
  alwaysShowControls: PropTypes.bool,

  /**
   * Give the player a label to be read by screen readers.
   */
  label: PropTypes.string,

  /**
   * The poster image to use before the media is played.
   */
  poster: PropTypes.string,

  /**
   * Label overrides for i18n. Defaults to english
   * See src/constants/translated/translations.js for default values
   */
  translations: PropTypes.shape(TranslationPropTypes),

  /**
   * Children of the <Player />
   */
  children: ChildrenPropTypes.oneOf([PlayerOverlay, WrappedPlayerOverlay]),

  /**
   * indicates the player should fill the width of its container
   */
  fluidWidth: PropTypes.bool,

  /**
   * indicates the player should fill the height of its container
   */
  fluidHeight: PropTypes.bool,

  /**
   * Reference to actions object
   */
  actionsRef: PropTypes.func,

  /**
   * Disable right click on the player container
   */
  disableRightClick: PropTypes.bool,
  captionPosition: Captions.propTypes.captionPosition,
  captionOffset: Captions.propTypes.captionOffset,
  autoShowCaption: PropTypes.string,

  /**
   * Make YouTube iframe unfocusable
   */
  disableYoutubeFocus: PropTypes.bool,

  /**
   * Hide control bar
   */
  hideControls: PropTypes.bool,
  getSettings: PropTypes.func,
  savePlayerSettings: PropTypes.func,
  startAt: PropTypes.number
}, playerEventsTypeSet), rendererEventsTypeSet), _class2.defaultProps = {
  sources: [],
  tracks: [],
  controls: PlayerControls => jsx(PlayerControls, null, jsx(PlayerControls.PlayPauseButton, {
    position: "start"
  }), jsx(PlayerControls.Timebar, null), jsx(PlayerControls.Volume, null), jsx(PlayerControls.PlayerSettings, null), jsx(PlayerControls.FullScreenButton, {
    position: "end"
  })),
  actionsRef: null,
  alwaysShowControls: false,
  children: null,
  poster: null,
  label: '',
  translations: {},
  fluidWidth: true,
  fluidHeight: false,
  disableRightClick: false,
  onControlsHidden: () => {},
  onControlsShown: () => {},
  captionPosition: Captions.defaultProps.captionPosition,
  captionOffset: Captions.defaultProps.captionOffset,
  autoShowCaption: null,
  disableYoutubeFocus: false,
  hideControls: false,
  getSettings: () => {},
  savePlayerSettings: () => {},
  startAt: 0
}, _class2.Marker = PlayerMarker, _class2.Overlay = WrappedPlayerOverlay, _class2.Playhead = PlayerPlayhead, _class2)) || _class);
export default Player;
export { Player, PlayerPropTypes };